import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import ReportView from "../components/ReportView";
import "../css/ParentDashboard.css";

const scoreStyle = (status) => {
  const base = { display: "inline-block", padding: "3px 12px", borderRadius: 20, fontSize: 12, fontWeight: 600 };
  switch ((status || "").toLowerCase()) {
    case "good":    return { ...base, background: "#dcfce7", color: "#166534" };
    case "average": return { ...base, background: "#fef9c3", color: "#854d0e" };
    case "low":     return { ...base, background: "#fee2e2", color: "#991b1b" };
    default:        return { ...base, background: "#f1f5f9", color: "#64748b" };
  }
};

const formatNextAssessment = (assessment) => {
  if (!assessment) return "-";
  if (new Date() >= new Date(assessment.next_assessment_date)) return "Available Now";
  return new Date(assessment.next_assessment_date).toDateString();
};

const getLabelClass = (label) => {
  switch (label) {
    case "Very Good":            return "label-verygood";
    case "Good":                 return "label-good";
    case "Need Attention":       return "label-attention";
    case "Consultant Required":  return "label-consult";
    default:                     return "label-nodata";
  }
};

const ParentDashboard = () => {
  const {
    parent, childData, fetchLearnerDashboard, learner, loading,
    linkLearner, fetchParentDashboard,
    fetchLearnerActivities, fetchLearnerAttendance, fetchLearnerReports,
    fetchReportData,
  } = useAuth();

  const navigate = useNavigate();
  const [selectedLearner, setSelectedLearner] = useState(null);
  const [tab, setTab] = useState("overview");
  const [learnerLoading, setLearnerLoading] = useState(false);
  const [error, setError] = useState("");

  /* link learner */
  const [linkCode, setLinkCode] = useState("");
  const [linkMsg, setLinkMsg] = useState("");
  const [linkLoading, setLinkLoading] = useState(false);

  /* activities / attendance / reports */
  const [activities, setActivities] = useState(null);
  const [attendance, setAttendance] = useState(null);
  const [reports, setReports] = useState(null);
  const [reportData, setReportData] = useState(null);
  const [reportLoading, setReportLoading] = useState(false);
  const [tabLoading, setTabLoading] = useState(false);

  if (loading) return <div className="loader">Loading...</div>;

  /* ---- select child ---- */
  const handleSelectLearner = async (child) => {
    setSelectedLearner(child);
    setTab("overview");
    setActivities(null);
    setAttendance(null);
    setReports(null);
    setError("");

    if (child.consent_status?.toLowerCase() !== "approved") {
      navigate("/parent/consent", { state: { learner: child } });
      return;
    }

    try {
      setLearnerLoading(true);
      await fetchLearnerDashboard(child.learner_id);
    } catch {
      setError("Failed to load learner data");
    } finally {
      setLearnerLoading(false);
    }
  };

  /* ---- tab switch ---- */
  const handleTab = async (t) => {
    setTab(t);
    if (!selectedLearner) return;
    const id = selectedLearner.learner_id;
    setTabLoading(true);
    try {
      if (t === "activities" && !activities) {
        const data = await fetchLearnerActivities(id);
        setActivities(Array.isArray(data) ? data : data?.activities || []);
      }
      if (t === "attendance" && !attendance) {
        const data = await fetchLearnerAttendance(id);
        setAttendance(data);
      }
      if (t === "reports" && !reports) {
        const data = await fetchLearnerReports(id);
        setReports(Array.isArray(data) ? data : data?.reports || []);
      }
      if (t === "report-view" && !reportData) {
        const data = await fetchReportData(id);
        setReportData(data);
      }
    } finally {
      setTabLoading(false);
    }
  };

  const handleDownloadReport = async () => {
    if (!selectedLearner) return;
    setReportLoading(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE || "https://beintelli.engaggio.com/handlers"}/user/parent/learner/${selectedLearner.learner_id}/reports/generate/assessment`,
        { method: "POST", headers: { Authorization: `Bearer ${token}` } }
      );
      const data = await res.json();
      if (data.file_path) window.open(`https://beintelli.engaggio.com${data.file_path}`, "_blank");
    } finally {
      setReportLoading(false);
    }
  };

  /* ---- link learner ---- */
  const handleLinkLearner = async () => {
    if (!linkCode.trim()) return setLinkMsg("Code enter చేయండి.");
    setLinkLoading(true);
    setLinkMsg("");
    try {
      const res = await linkLearner(linkCode.trim());
      if (res?.message) {
        setLinkMsg("✅ " + res.message);
        setLinkCode("");
        fetchParentDashboard();
      } else {
        setLinkMsg("❌ " + (res?.error || "Failed"));
      }
    } catch {
      setLinkMsg("❌ Request failed");
    } finally {
      setLinkLoading(false);
    }
  };

  return (
    <div className="parent-dashboard">
      {/* HEADER */}
      <header className="dashboard-header">
        <h1>Parent Dashboard</h1>
        <p>Welcome, {parent?.full_name}</p>
      </header>

      {/* ADD / LINK CHILD */}
      <section className="card">
        <h3>Add Child</h3>
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 16 }}>
          <button className="btn" onClick={() => navigate("/parent/consent")}>
            + Create New Child
          </button>
        </div>
      </section>

      {/* EMPTY STATE */}
      {childData?.length === 0 && (
        <div className="card"><p>No children added yet.</p></div>
      )}

      {/* CHILD LIST */}
      <section className="children-section">
        <h3>Your Children</h3>
        <div className="children-list">
          {childData?.map((child) => (
            <button
              key={child.learner_id}
              type="button"
              className={`child-card ${selectedLearner?.learner_id === child.learner_id ? "active" : ""}`}
              onClick={() => handleSelectLearner(child)}
            >
              <h4>{child.full_name}</h4>
              <p>Grade: {child.grade}</p>
              {child.age < 6 && <span className="tag">Parent Assisted</span>}
              <span className={`badge ${child.consent_status}`}>{child.consent_status}</span>
            </button>
          ))}
        </div>
      </section>

      {learnerLoading && <div className="card">Loading learner data...</div>}
      {error && <div className="card error">{error}</div>}

      {/* SELECTED CHILD DETAIL */}
      {selectedLearner && learner && (
        <>
          {/* start assessment for young child */}
          {selectedLearner.age < 6 && (
            <section className="card highlight">
              <h2>{selectedLearner.full_name}</h2>
              <button
                className="btn"
                onClick={() => navigate("/child/assessment", { state: { learner: selectedLearner, mode: "parent-assisted" } })}
              >
                Start Assessment (Assist Child)
              </button>
            </section>
          )}

          {/* TABS */}
          <div className="tabs" style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "16px 0 0" }}>
            {[
              { key: "overview",     label: "Overview" },
              { key: "activities",   label: "Activities" },
              { key: "attendance",   label: "Attendance" },
              { key: "reports",      label: "Past Reports" },
              { key: "report-view",  label: "View Report" },
            ].map(({ key, label }) => (
              <button
                key={key}
                onClick={() => handleTab(key)}
                style={{
                  padding: "8px 18px", borderRadius: 8, border: "none", cursor: "pointer",
                  fontWeight: 600, fontSize: 13,
                  background: tab === key ? "#1e293b" : "#f1f5f9",
                  color: tab === key ? "#fff" : "#475569",
                }}
              >
                {label}
              </button>
            ))}
          </div>

          {tabLoading && <div className="card">Loading...</div>}

          {/* OVERVIEW TAB */}
          {tab === "overview" && !tabLoading && (
            <>
              <section className="card summary">
                <div>
                  <h4>Last Assessment</h4>
                  <p>{learner?.latest_assessment ? new Date(learner.latest_assessment.date).toDateString() : "No assessment yet"}</p>
                </div>
                <div>
                  <h4>Next Assessment</h4>
                  <p>{formatNextAssessment(learner?.latest_assessment)}</p>
                </div>
                {learner?.latest_assessment?.overall_status && (
                  <div>
                    <h4>Engagement Status</h4>
                    <span style={scoreStyle(learner.latest_assessment.overall_status)}>
                      {learner.latest_assessment.overall_status}
                    </span>
                  </div>
                )}
              </section>

            </>
          )}

          {/* ACTIVITIES TAB */}
          {tab === "activities" && !tabLoading && (
            <section className="card">
              <h3>Activities</h3>
              {activities?.length ? (
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Title</th>
                      <th style={s.th}>Status</th>
                      <th style={s.th}>Grade</th>
                      <th style={s.th}>Date</th>
                    </tr>
                  </thead>
                  <tbody>
                    {activities.map((a, i) => (
                      <tr key={a.id || i} style={s.tr}>
                        <td style={s.td}>{a.title || a.activity_title || "—"}</td>
                        <td style={s.td}>{a.status || "—"}</td>
                        <td style={s.td}>{a.grade ?? "—"}</td>
                        <td style={s.td}>{a.created_at ? new Date(a.created_at).toDateString() : "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: "#94a3b8" }}>No activities yet.</p>
              )}
            </section>
          )}

          {/* ATTENDANCE TAB */}
          {tab === "attendance" && !tabLoading && (
            <section className="card">
              <h3>Attendance</h3>
              <div style={{ display: "flex", gap: 24, flexWrap: "wrap", marginBottom: 16 }}>
                <div style={s.statBox}>
                  <span style={s.statNum}>{attendance?.present ?? 0}</span>
                  <span style={s.statLabel}>Present</span>
                </div>
                <div style={s.statBox}>
                  <span style={s.statNum}>{attendance?.absent ?? 0}</span>
                  <span style={s.statLabel}>Absent</span>
                </div>
                <div style={s.statBox}>
                  <span style={s.statNum}>{attendance?.total ?? 0}</span>
                  <span style={s.statLabel}>Total Days</span>
                </div>
              </div>
              {attendance?.records?.length ? (
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Status</th>
                      <th style={s.th}>Check-in</th>
                      <th style={s.th}>Check-out</th>
                    </tr>
                  </thead>
                  <tbody>
                    {attendance.records.map((r) => (
                      <tr key={r.id || r.date} style={s.tr}>
                        <td style={s.td}>{new Date(r.date).toDateString()}</td>
                        <td style={s.td}>{r.status}</td>
                        <td style={s.td}>{r.checkin_time || "—"}</td>
                        <td style={s.td}>{r.checkout_time || "—"}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: "#94a3b8" }}>No attendance records.</p>
              )}
            </section>
          )}

          {/* PAST REPORTS TAB */}
          {tab === "reports" && !tabLoading && (
            <section className="card">
              <h3>Past Reports</h3>
              {reports?.length ? (
                <table style={s.table}>
                  <thead>
                    <tr>
                      <th style={s.th}>Report</th>
                      <th style={s.th}>Date</th>
                      <th style={s.th}>Download</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reports.map((r) => (
                      <tr key={r.id || r.report_name} style={s.tr}>
                        <td style={s.td}>{r.report_name}</td>
                        <td style={s.td}>{new Date(r.created_at).toDateString()}</td>
                        <td style={s.td}>
                          <a href={`https://beintelli.engaggio.com${r.file_path}`} target="_blank" rel="noreferrer">
                            Download
                          </a>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              ) : (
                <p style={{ color: "#94a3b8" }}>No reports available.</p>
              )}
            </section>
          )}

          {/* REPORT VIEW TAB */}
          {tab === "report-view" && !tabLoading && (
            reportData
              ? <ReportView
                  data={reportData}
                  downloadLoading={reportLoading}
                  onDownload={handleDownloadReport}
                />
              : <section className="card">
                  <p style={{ color: "#94a3b8" }}>No report available yet. Complete an assessment first.</p>
                </section>
          )}
        </>
      )}
    </div>
  );
};

const s = {
  table:   { width: "100%", borderCollapse: "collapse" },
  th:      { textAlign: "left", padding: "10px 12px", fontSize: 12, fontWeight: 700, color: "#64748b", borderBottom: "1px solid #e2e8f0", textTransform: "uppercase" },
  tr:      { borderBottom: "1px solid #f1f5f9" },
  td:      { padding: "12px 12px", fontSize: 14, color: "#1e293b" },
  statBox: { display: "flex", flexDirection: "column", alignItems: "center", background: "#f8fafc", borderRadius: 12, padding: "16px 24px", minWidth: 80 },
  statNum: { fontSize: 28, fontWeight: 800, color: "#1e293b" },
  statLabel: { fontSize: 12, color: "#64748b", marginTop: 4 },
};

export default ParentDashboard;
